import React, { useCallback, useEffect, useState, useRef } from "react";
import { useLocation } from "react-router-dom";
import _ from "lodash";

/** Libraries */
import { Search, Close } from "@libraries/icons";
import Store from "@store/jotai";

// Languages
import { GlobalMessages } from "@languages/interfaces";
import useTranslation from "@languages/useTranslation";

import * as Styled from "./styles";

interface Props {
  searchIsActive: boolean;
  setSearchIsActive: React.Dispatch<React.SetStateAction<boolean>>;
}

const REGEX_VALID_DATE = /([0-9]{4})-([0-9]{2})-([0-9]{2})T([0-9]{2}):([0-9]{2}):([0-9]{2})(\.[0-9]{3})?Z/g;

const SearchInput: React.FC<Props> = ({
  searchIsActive,
  setSearchIsActive
}) => {
  const { t } = useTranslation();
  const [searchValue, setSearchValue] = useState("");
  const [showClearButton, setShowClearButton] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const { value: clients, setFilteredClients } = Store.useClients();
  const { value: invoices, setFilteredInvoices } = Store.useInvoices();
  const { value: contracts, setFilteredContracts } = Store.useContracts();
  const { value: concretings, setFilteredConcretings } = Store.useConcretings();
  const { value: concretingRequests, setFilteredConcretingRequests } = Store.useConcretingRequests();
  const { value: users, setFilteredUsers } = Store.useUsers();
  const { value: permissionsUsers, setFilteredPermissionsUsers } = Store.usePermissionsUsers();
  const { value: certificates, setFilteredCertificates } = Store.useCertificates();

  const location = useLocation();

  const removeUnusedFields = useCallback((object: any) => _.omit(object, [
    "id",
    "__typename",
    "status",
    "createdAt",
    "updatedAt",
    "permissions"
  ]), []);

  const checkPropertiesToFilter = useCallback(
    (object: any, search: string) => {
      const objectWithoutUnusedFields = removeUnusedFields(object);

      const hasObject = Object.values(objectWithoutUnusedFields).filter(
        (value) => {
          if (typeof value === "object") {
            return checkPropertiesToFilter(value, search);
          }

          if (
            typeof value === "string"
            && value.match(new RegExp(REGEX_VALID_DATE))
          ) {
            const valueToDate = new Date(value);

            if (valueToDate.toString() !== "Invalid Date") {
              return valueToDate.toLocaleString().includes(search);
            }
          }

          return (value || "").toString().toLowerCase().includes(search);
        }
      );

      return hasObject.length > 0;
    },
    [removeUnusedFields]
  );

  const makeFilter = useCallback(
    (value: string) => {
      const valueToLower = value.toLowerCase();

      if (location.pathname === "/clients") {
        const filter = clients.filter((client) => {
          const objectWithoutUnusedFields = removeUnusedFields(client);

          return checkPropertiesToFilter(
            objectWithoutUnusedFields,
            valueToLower
          );
        });

        setFilteredClients(filter);
      }

      if (location.pathname === "/certificates") {
        const filter = certificates.filter((certificate) => {
          const objectWithoutUnusedFields = removeUnusedFields(certificate);

          return checkPropertiesToFilter(
            objectWithoutUnusedFields,
            valueToLower
          );
        });

        setFilteredCertificates(filter);
      }

      if (location.pathname === "/invoices") {
        const filter = invoices.filter((invoice) => {
          const objectWithoutUnusedFields = removeUnusedFields(invoice);

          return checkPropertiesToFilter(
            objectWithoutUnusedFields,
            valueToLower
          );
        });

        setFilteredInvoices(filter);
      }

      if (location.pathname === "/contracts") {
        const filter = contracts.filter((contract) => {
          const objectWithoutUnusedFields = removeUnusedFields(contract);

          return checkPropertiesToFilter(
            objectWithoutUnusedFields,
            valueToLower
          );
        });

        setFilteredContracts(filter);
      }

      if (location.pathname === "/concretings") {
        const filter = concretings.filter((concreting) => {
          const objectWithoutUnusedFields = removeUnusedFields(concreting);

          return checkPropertiesToFilter(
            objectWithoutUnusedFields,
            valueToLower
          );
        });

        setFilteredConcretings(filter);
      }

      if (location.pathname === "/permissions") {
        const filter = users.filter((user) => {
          const objectWithoutUnusedFields = removeUnusedFields(user);

          return checkPropertiesToFilter(
            objectWithoutUnusedFields,
            valueToLower
          );
        });

        setFilteredUsers(filter);
      }

      if (location.pathname === "/permission-user") {
        const filter = permissionsUsers.filter((permissionUser) => {
          const objectWithoutUnusedFields = removeUnusedFields(permissionUser);

          return checkPropertiesToFilter(
            objectWithoutUnusedFields,
            valueToLower
          );
        });

        setFilteredPermissionsUsers(filter);
      }

      if (
        location.pathname === "/concreting-requests"
        || location.pathname === "/concreting-approvals"
      ) {
        const filter = concretingRequests.filter((concretingRequest) => {
          const objectWithoutUnusedFields = removeUnusedFields(concretingRequest);

          return checkPropertiesToFilter(
            objectWithoutUnusedFields,
            valueToLower
          );
        });

        setFilteredConcretingRequests(filter);
      }
    },
    [
      location.pathname,
      clients,
      setFilteredClients,
      removeUnusedFields,
      checkPropertiesToFilter,
      certificates,
      setFilteredCertificates,
      invoices,
      setFilteredInvoices,
      contracts,
      setFilteredContracts,
      concretings,
      setFilteredConcretings,
      users,
      setFilteredUsers,
      permissionsUsers,
      setFilteredPermissionsUsers,
      concretingRequests,
      setFilteredConcretingRequests
    ]
  );

  const removeFilter = useCallback(() => {
    switch (location.pathname) {
      case "/clients":
        setFilteredClients(clients);
        break;
      case "/certificates":
        setFilteredCertificates(certificates);
        break;
      case "/invoices":
        setFilteredInvoices(invoices);
        break;
      case "/contracts":
        setFilteredContracts(contracts);
        break;
      case "/concretings":
        setFilteredConcretings(concretings);
        break;
      case "/permissions":
        setFilteredUsers(users);
        break;
      case "/permission-user":
        setFilteredPermissionsUsers(permissionsUsers);
        break;
      case "/concreting-requests":
        setFilteredConcretingRequests(concretingRequests);
        break;
      case "/concreting-approvals":
        setFilteredConcretingRequests(concretingRequests);
        break;
      default:
        break;
    }
  }, [
    location.pathname,
    clients,
    certificates,
    invoices,
    contracts,
    concretings,
    users,
    permissionsUsers,
    concretingRequests,
    setFilteredClients,
    setFilteredCertificates,
    setFilteredInvoices,
    setFilteredContracts,
    setFilteredConcretings,
    setFilteredUsers,
    setFilteredPermissionsUsers,
    setFilteredConcretingRequests
  ]);

  const handleSearchChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setSearchValue(event.target.value);
      setShowClearButton(event.target.value.length > 0);

      if (event.target.value.length > 0) {
        makeFilter(event.target.value);
      } else {
        removeFilter();
      }
    },
    [makeFilter, removeFilter]
  );

  const handleSearchClear = useCallback(() => {
    removeFilter();
    setSearchValue("");
    setShowClearButton(false);
  }, [removeFilter]);

  const handleBlurInput = useCallback(() => {
    if (searchValue.length > 0) return;

    setSearchIsActive(false);
  }, [searchValue, setSearchIsActive]);

  useEffect(() => {
    if (searchIsActive) {
      inputRef.current?.focus();
    }
  }, [searchIsActive]);

  return (
    <Styled.Container>
      <input
        ref={inputRef}
        type="text"
        placeholder={t(GlobalMessages.searchText)}
        value={searchValue}
        onChange={handleSearchChange}
        onBlur={handleBlurInput}
      />
      {showClearButton ? (
        <Close onClick={handleSearchClear} />
      ) : (
        <Search onClick={() => setSearchIsActive(!searchIsActive)} />
      )}
    </Styled.Container>
  );
};

export default SearchInput;
